import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import AppIconButton from "../Common/Button/IconButton";
import { LabelText, SectionSubHeading } from "../Common/Typography/AppTypography";
import Alert from "../layout/Alert";
import Dialog from "../Common/Dailog/Dailog";
import { setAlert } from "../../action/alert";
import {
  uploadAsset,
  updateAsset,
  deleteAsset,
  getAssetMetadata,
  extractAssetId,
  getAssetErrorMessage,
  parseAssetTagMetadata,
  groupResolutionsByTag,
  loadAssetImagePreview,
} from "../../action/asset";
import { selectMenuProps } from "../../utils/selectMenuProps";
import customConfig from "../../config";
import {
  ASSET_RESOLUTION_OPTIONS,
  fileInputStyle,
  formControlSx,
} from "./assetConstants";
import AssetPreview, { LocalFilePreview } from "./AssetPreview";

const normalizeAssetTags = (tags) =>
  (Array.isArray(tags) ? tags : [])
    .map((entry) => ({
      tag: typeof entry?.tag === "string" ? entry.tag.trim() : "",
      resolutions: Array.isArray(entry?.resolutions)
        ? entry.resolutions.map(String).filter(Boolean)
        : [],
    }))
    .filter((entry) => entry.tag);

const storedResolutionCellSx = {
  px: 2,
  py: 1,
  textAlign: "left",
  verticalAlign: "middle",
  typography: "body2",
  borderBottom: "1px solid",
  borderColor: "divider",
};

function StoredResolutionThumb({ url }) {
  return (
    <Box
      sx={{
        width: 72,
        height: 48,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "action.hover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {url ? (
        <Box
          component="img"
          src={url}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      )}
    </Box>
  );
}

const assetHasResolution = (entries, resolution) =>
  entries.some((entry) =>
    (entry.resolutions || []).some((item) => item === resolution)
  );

const appendOptionalTag = (formData, value) => {
  if (value) {
    formData.append("tag", value);
  }
};

const pageFieldSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  display: "block",
  boxSizing: "border-box",
  "& .MuiOutlinedInput-root": {
    width: "100%",
    maxWidth: "100%",
    minWidth: "0 !important",
    boxSizing: "border-box",
    height: 42,
    minHeight: 42,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    maxWidth: "100%",
  },
  "& .MuiInputBase-input": {
    boxSizing: "border-box !important",
    width: "100% !important",
    maxWidth: "100% !important",
    minWidth: "0 !important",
  },
  "& .MuiFormHelperText-root": {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    mx: 0,
  },
};

const pageFileInputStyle = {
  ...fileInputStyle,
  display: "block",
  width: "100%",
  maxWidth: "100%",
  height: 42,
  margin: 0,
  boxSizing: "border-box",
};

const pageGridSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  mb: 3,
  alignItems: "flex-start",
};

const pageActionsSx = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  gap: 1.5,
  flexWrap: "wrap",
  width: "100%",
  minWidth: 0,
  "& .MuiButton-root": {
    width: { xs: "100%", sm: "auto" },
  },
};

const PageField = ({ label, children }) => (
  <Grid2 size={{ xs: 12, md: 4 }} sx={{ minWidth: 0, maxWidth: "100%" }}>
    <LabelText sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>{label}</LabelText>
    <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>{children}</Box>
  </Grid2>
);

function AssetUploadForm({
  mode = "create",
  initialValues = null,
  onSuccess,
  onCancel,
  embedded = false,
  page = false,
}) {
  const isUpdate = mode === "update";
  const dispatch = useDispatch();
  const [assetId, setAssetId] = useState(initialValues?.assetId || "");
  const [assetName, setAssetName] = useState(initialValues?.name || "");
  const [description, setDescription] = useState(initialValues?.description || "");
  const initialTags = isUpdate ? normalizeAssetTags(initialValues?.tags) : [];
  const initialStoredResolutions = [
    ...new Set(initialTags.flatMap((entry) => entry.resolutions || []).filter(Boolean)),
  ];
  const [existingTags, setExistingTags] = useState(initialTags);
  const [resolution, setResolution] = useState(initialStoredResolutions[0] || "");
  const [selectedResolutions, setSelectedResolutions] = useState(initialStoredResolutions);
  const [storedRows, setStoredRows] = useState([]);
  const [storedGroups, setStoredGroups] = useState([]);
  const [deleteRow, setDeleteRow] = useState(null);
  const [storedReload, setStoredReload] = useState(0);
  const [tag, setTag] = useState(initialTags[0]?.tag || "");
  const [file, setFile] = useState(null);
  const [storedPreviewUrl, setStoredPreviewUrl] = useState("");
  const [storedPreviewFile, setStoredPreviewFile] = useState(null);
  const [storedPreviewLoading, setStoredPreviewLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState("");
  const [savedAssetId, setSavedAssetId] = useState("");
  const [assetIdCopied, setAssetIdCopied] = useState(false);
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceTargetRef = useRef(null);
  const groupedPreviewsRef = useRef([]);
  const baselineTagRef = useRef((initialTags[0]?.tag || "").trim());
  const baselineResolutionRef = useRef(initialTags[0]?.resolutions?.[0] || "");
  const loading = Boolean(pendingAction);
  const fieldSx = page
    ? { width: "100%", maxWidth: { xs: "100%", md: 640 } }
    : formControlSx;
  const trimmedAssetId = assetId?.trim() || "";

  const clearFileInput = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] || null);
  };

  useEffect(() => {
    if (!isUpdate) {
      return undefined;
    }

    const assetIdValue = initialValues?.assetId?.trim();

    if (!assetIdValue) {
      return undefined;
    }

    let cancelled = false;

    const loadStoredTags = async () => {
      try {
        const res = await getAssetMetadata(assetIdValue);
        const groups = groupResolutionsByTag(res?.data);
        const entries = groups
          .filter((entry) => entry.tag)
          .map((entry) => ({ tag: entry.tag, resolutions: entry.resolutions }));
        const storedResolutions = [
          ...new Set(groups.flatMap((entry) => entry.resolutions)),
        ];
        const rows = groups.flatMap((group) =>
          (group.resolutions || []).map((value) => ({
            tag: group.tag || "",
            resolution: value,
            key: `${group.tag || ""}::${value}`,
          }))
        );
        const fileGroups = groups.flatMap((group) => {
          const files = group.files?.length
            ? group.files
            : (group.resolutions || []).map((resolution) => ({ resolutions: [resolution] }));

          return files
            .filter((file) => file.resolutions?.length)
            .map((file) => ({
              tag: group.tag || "",
              resolutions: file.resolutions,
              resolution: file.resolutions[0],
              key: `${group.tag || ""}::${file.resolutions.join(",")}`,
            }));
        });
        const metadata = res?.data && typeof res.data === "object" && !Array.isArray(res.data)
          ? res.data
          : {};

        if (cancelled) {
          return;
        }

        if (entries.length) {
          setExistingTags(entries);
          setTag((current) => {
            const next = current || entries[0].tag;
            baselineTagRef.current = next.trim();
            return next;
          });
        }

        const previewGroups = await Promise.all(fileGroups.map(async (group) => {
          try {
            const preview = await loadAssetImagePreview(assetIdValue, "", group.resolution);

            return {
              ...group,
              url: preview.url || "",
              revoke: Boolean(preview.revoke),
            };
          } catch {
            return { ...group, url: "", revoke: false };
          }
        }));

        if (cancelled) {
          previewGroups.forEach((group) => {
            if (group.revoke && group.url) {
              URL.revokeObjectURL(group.url);
            }
          });
          return;
        }

        groupedPreviewsRef.current.forEach((group) => {
          if (group.revoke && group.url) {
            URL.revokeObjectURL(group.url);
          }
        });

        groupedPreviewsRef.current = previewGroups;
        setStoredRows(rows);
        setStoredGroups(previewGroups);
        setSelectedResolutions(storedResolutions);

        if (storedResolutions.length) {
          setResolution((current) => {
            if (current && storedResolutions.includes(current)) {
              return current;
            }

            const next = storedResolutions[0];
            baselineResolutionRef.current = next;
            return next;
          });
        } else {
          setResolution("");
          baselineResolutionRef.current = "";
        }

        const metadataName = String(metadata.name ?? metadata.assetName ?? "").trim();
        const metadataDescription = String(metadata.description ?? "").trim();

        if (metadataName) {
          setAssetName((current) => current || metadataName);
        }

        if (metadataDescription) {
          setDescription((current) => current || metadataDescription);
        }
      } catch {
        // Tag metadata is optional here. Leave the file fields empty when it cannot be loaded.
      }
    };

    loadStoredTags();

    return () => {
      cancelled = true;
      groupedPreviewsRef.current.forEach((group) => {
        if (group.revoke && group.url) {
          URL.revokeObjectURL(group.url);
        }
      });
      groupedPreviewsRef.current = [];
    };
  }, [isUpdate, initialValues?.assetId, storedReload]);

  useEffect(() => {
    if (!isUpdate || !trimmedAssetId || !resolution) {
      if (!isUpdate) {
        setStoredPreviewUrl("");
        setStoredPreviewFile(null);
        setStoredPreviewLoading(false);
      }
      return undefined;
    }

    let cancelled = false;
    let revokeUrl = "";
    setStoredPreviewLoading(true);

    const loadStoredPreview = async () => {
      try {
        const preview = await loadAssetImagePreview(trimmedAssetId, "", resolution);
        if (cancelled) {
          if (preview.revoke && preview.url) {
            URL.revokeObjectURL(preview.url);
          }
          return;
        }

        let nextFile = preview.file || null;

        if (cancelled) {
          if (preview.revoke && preview.url) {
            URL.revokeObjectURL(preview.url);
          }
          return;
        }

        if (preview.revoke && preview.url) {
          revokeUrl = preview.url;
        }
        setStoredPreviewUrl(preview.url || "");
        setStoredPreviewFile(nextFile);
      } catch {
        if (!cancelled) {
          setStoredPreviewUrl("");
          setStoredPreviewFile(null);
        }
      } finally {
        if (!cancelled) {
          setStoredPreviewLoading(false);
        }
      }
    };

    loadStoredPreview();

    return () => {
      cancelled = true;
      if (revokeUrl) {
        URL.revokeObjectURL(revokeUrl);
      }
    };
  }, [isUpdate, trimmedAssetId, resolution]);

  const imagePreview = (
    <>
      <LocalFilePreview file={file} />
      {isUpdate && !file ? (
        <AssetPreview url={storedPreviewUrl} loading={storedPreviewLoading} />
      ) : null}
    </>
  );

  const resetForm = () => {
    const storedTags = existingTags.length
      ? existingTags
      : normalizeAssetTags(initialValues?.tags);

    setAssetId(isUpdate ? initialValues?.assetId || "" : "");
    setAssetName(isUpdate ? initialValues?.name || "" : "");
    setDescription(isUpdate ? initialValues?.description || "" : "");
    const nextTag = isUpdate ? storedTags[0]?.tag || "" : "";
    const nextResolution = isUpdate ? storedTags[0]?.resolutions?.[0] || "" : "";
    const nextResolutions = isUpdate
      ? [...new Set(storedRows.map((row) => row.resolution).filter(Boolean))]
      : [];
    setTag(nextTag);
    setResolution(nextResolution);
    setSelectedResolutions(nextResolutions.length ? nextResolutions : (isUpdate && nextResolution ? [nextResolution] : []));
    baselineTagRef.current = nextTag.trim();
    baselineResolutionRef.current = nextResolution;
    setSavedAssetId("");
    setAssetIdCopied(false);
    setPendingAction("");
    clearFileInput();
  };

  const validateFileFields = () => {
    if (isUpdate && !trimmedAssetId) {
      dispatch(setAlert("Asset ID Cannot be Empty", "danger"));
      return false;
    }

    if (!isUpdate && !selectedResolutions.length && !resolution) {
      dispatch(setAlert("Resolution Cannot be Empty", "danger"));
      return false;
    }

    if (!file && !isUpdate) {
      dispatch(setAlert("Please select a file to upload.", "danger"));
      return false;
    }

    return true;
  };

  const handleFileSubmit = async (event) => {
    event.preventDefault();
    setSavedAssetId("");
    setAssetIdCopied(false);

    if (!validateFileFields()) {
      return;
    }

    const trimmedTag = tag.trim();
    const trimmedName = assetName?.trim() || "";
    const trimmedDescription = description?.trim() || "";
    const chosenResolutions = selectedResolutions.length
      ? selectedResolutions
      : (page && isUpdate ? [] : (resolution ? [resolution] : []));
    const resolutionPayload = chosenResolutions.join(",");
    setPendingAction("file");

    try {
      const inputFile = fileInputRef.current?.files?.[0];
      const pickedFile = [file, inputFile].find((item) => item && item.size > 0) || null;

      if (!isUpdate && !pickedFile) {
        dispatch(setAlert("Please select a file to upload.", "danger"));
        return;
      }

      const storedResolutionValues = [
        ...new Set(storedRows.map((row) => row.resolution).filter(Boolean)),
      ];
      const selectionMatchesStored =
        chosenResolutions.length === storedResolutionValues.length &&
        chosenResolutions.every((value) => storedResolutionValues.includes(value));

      if (isUpdate && page && chosenResolutions.length && !pickedFile && !selectionMatchesStored) {
        dispatch(setAlert("Please select a file to upload.", "danger"));
        return;
      }

      if (isUpdate && page && pickedFile && !chosenResolutions.length) {
        dispatch(setAlert("Resolution Cannot be Empty", "danger"));
        return;
      }

      if (isUpdate && !page && pickedFile && !resolution) {
        dispatch(setAlert("Resolution Cannot be Empty", "danger"));
        return;
      }

      const resolutionChanged = isUpdate && !page && resolution !== baselineResolutionRef.current;

      if (isUpdate && !page && !pickedFile && resolutionChanged) {
        dispatch(setAlert("Please select a file to upload.", "danger"));
        return;
      }

      let assetAlreadyExists = false;

      if (!isUpdate && trimmedAssetId && !selectedResolutions.length) {
        try {
          const metadata = await getAssetMetadata(trimmedAssetId);
          const entries = parseAssetTagMetadata(metadata?.data);
          assetAlreadyExists = true;

          if (assetHasResolution(entries, resolution)) {
            dispatch(
              setAlert(
                "This asset already has an image for this resolution.",
                "danger"
              )
            );
            return;
          }
        } catch (err) {
          const status = err?.response?.status;
          const message = err?.response?.data?.message || err?.message || "";
          const notFound =
            status === 404 ||
            (typeof message === "string" &&
              (message.toLowerCase().includes("not found") ||
                message.toLowerCase().includes("does not exist")));

          if (!notFound) {
            dispatch(setAlert(getAssetErrorMessage(err), "danger"));
            return;
          }
        }
      }

      let res;

      if (isUpdate) {
        const metadataData = new FormData();
        metadataData.append("assetId", trimmedAssetId);
        metadataData.append("name", trimmedName);
        metadataData.append("description", trimmedDescription);
        res = await updateAsset(metadataData);

        if (pickedFile) {
          const fileData = new FormData();
          fileData.append("file", pickedFile, pickedFile.name || "asset");
          fileData.append("assetId", trimmedAssetId);
          appendOptionalTag(fileData, trimmedTag);
          fileData.append("resolution", resolutionPayload);
          res = await updateAsset(fileData);
          baselineTagRef.current = trimmedTag;
          baselineResolutionRef.current = resolution;
        }
      } else if (assetAlreadyExists) {
        const fileData = new FormData();
        fileData.append("file", pickedFile, pickedFile.name || "asset");
        fileData.append("assetId", trimmedAssetId);
        appendOptionalTag(fileData, trimmedTag);
        fileData.append("resolution", resolution);
        res = await updateAsset(fileData);
      } else {
        const formData = new FormData();
        formData.append("file", pickedFile, pickedFile.name || "asset");
        formData.append("resolution", resolutionPayload);
        appendOptionalTag(formData, trimmedTag);

        if (trimmedName) {
          formData.append("name", trimmedName);
        }

        if (trimmedDescription) {
          formData.append("description", trimmedDescription);
        }

        res = await uploadAsset(formData);
      }
      const responseAssetId = extractAssetId(res?.data, trimmedAssetId);

      dispatch(
        setAlert(
          isUpdate ? customConfig.UPDATE_SUCCESS : customConfig.ADD_SUCCESS,
          "success"
        )
      );

      if (isUpdate) {
        onSuccess?.({
          type: pickedFile ? "file" : "metadata",
          assetId: responseAssetId || trimmedAssetId,
          name: trimmedName,
          description: trimmedDescription,
        });
      } else {
        setResolution("");
        setSelectedResolutions([]);
        setTag("");
        clearFileInput();
        setSavedAssetId(responseAssetId);
        onSuccess?.({
          type: "create",
          assetId: responseAssetId,
          name: trimmedName,
          description: trimmedDescription,
        });
      }
    } catch (err) {
      dispatch(setAlert(getAssetErrorMessage(err), "danger"));
    } finally {
      setPendingAction("");
    }
  };

  const handleCopyAssetId = async () => {
    if (!savedAssetId) {
      return;
    }

    try {
      await navigator?.clipboard?.writeText(savedAssetId);
      setAssetIdCopied(true);
      setTimeout(() => setAssetIdCopied(false), 3000);
    } catch (err) {
      dispatch(setAlert("Failed to copy Asset ID", "danger"));
    }
  };

  const assetIdField = (
    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
      <Grid2 size={{ xs: 12, md: 2 }}>
        <LabelText sx={{ fontWeight: 600 }}>Asset ID:</LabelText>
      </Grid2>
      <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
        <FormInput
          type="text"
          size="small"
          fullWidth
          label={isUpdate ? "Asset ID" : "Enter Asset ID"}
          placeholder="e.g. home-banner-test"
          value={assetId}
          onChange={(e) => setAssetId(e.target.value)}
          disabled={loading}
          helperText="Optional. Sent with the file when you already have an Asset ID."
          sx={fieldSx}
        />
      </Grid2>
    </Grid2>
  );

  const metadataFields = (
    <>
      <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
        <Grid2 size={{ xs: 12, md: 2 }}>
          <LabelText sx={{ fontWeight: 600 }}>Asset Name:</LabelText>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
          <FormInput
            type="text"
            size="small"
            fullWidth
            label="Enter Asset Name"
            placeholder="e.g. Sleep Profile"
            value={assetName}
            onChange={(e) => setAssetName(e.target.value)}
            disabled={loading}
            helperText="Optional"
            sx={fieldSx}
          />
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "flex-start" }}>
        <Grid2 size={{ xs: 12, md: 2 }}>
          <LabelText sx={{ fontWeight: 600, pt: 1 }}>Description:</LabelText>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
          <FormInput
            type="textarea"
            size="small"
            fullWidth
            rows={3}
            label="Enter Description"
            placeholder="e.g. Sleep profile images"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
            helperText="Optional"
            sx={fieldSx}
          />
        </Grid2>
      </Grid2>
    </>
  );

  const fileFields = (
    <>
      <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
        <Grid2 size={{ xs: 12, md: 2 }}>
          <LabelText sx={{ fontWeight: 600 }}>Tag:</LabelText>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
          <FormInput
            type="text"
            size="small"
            fullWidth
            label="Enter Tag"
            placeholder="Optional"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            disabled={loading}
            helperText="Optional"
            sx={fieldSx}
          />
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
        <Grid2 size={{ xs: 12, md: 2 }}>
          <LabelText sx={{ fontWeight: 600 }}>Resolution:</LabelText>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
          <FormInput
            type="select"
            size="small"
            fullWidth
            label="Select Resolution"
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            options={ASSET_RESOLUTION_OPTIONS}
            MenuProps={selectMenuProps}
            disabled={loading}
            sx={fieldSx}
          />
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} sx={{ mb: 3, alignItems: "center" }}>
        <Grid2 size={{ xs: 12, md: 2 }}>
          <LabelText sx={{ fontWeight: 600 }}>File Upload:</LabelText>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
          <Box sx={fieldSx}>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              style={fileInputStyle}
              disabled={loading}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              Any file type (images, animations, JSON, video, etc.)
            </Typography>
            {file && (
              <Typography variant="body2" sx={{ mt: 1, color: "text.primary" }}>
                <strong>Selected File:</strong> {file.name}
                {file.type ? ` (${file.type})` : ""}
              </Typography>
            )}
            {imagePreview}
          </Box>
        </Grid2>
      </Grid2>
    </>
  );

  const savedAssetIdRow = !isUpdate && savedAssetId ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 2,
        color: "success.main",
      }}
    >
      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
        <strong>Asset ID:</strong> {savedAssetId}
      </Typography>
      <AppIconButton
        size="small"
        variant="ghost"
        tooltip="Copy Asset ID"
        onClick={handleCopyAssetId}
        icon={
          assetIdCopied ? (
            <CheckCircleOutlineIcon fontSize="small" />
          ) : (
            <FileCopyIcon fontSize="small" />
          )
        }
      />
    </Box>
  ) : null;

  const resolutionOptions = ASSET_RESOLUTION_OPTIONS.slice();

  existingTags.forEach((entry) => {
    entry.resolutions.forEach((value) => {
      if (!resolutionOptions.some((option) => option.value === value)) {
        resolutionOptions.push({ value, label: value });
      }
    });
  });

  storedRows.forEach((row) => {
    if (row.resolution && !resolutionOptions.some((option) => option.value === row.resolution)) {
      resolutionOptions.push({ value: row.resolution, label: row.resolution });
    }
  });

  const handleStoredTagChange = (event) => {
    setTag(event.target.value);
  };

  const replacingResolutions = selectedResolutions.filter((value) =>
    storedRows.some((row) => row.resolution === value)
  );

  const startReplace = (row) => {
    replaceTargetRef.current = row;
    if (replaceInputRef.current) {
      replaceInputRef.current.value = "";
      replaceInputRef.current.click();
    }
  };

  const handleReplaceFile = async (event) => {
    const pickedFile = event.target.files?.[0];
    const target = replaceTargetRef.current;
    event.target.value = "";

    if (!pickedFile || !target || !trimmedAssetId) {
      return;
    }

    setPendingAction("replace");

    try {
      const fileData = new FormData();
      fileData.append("file", pickedFile, pickedFile.name || "asset");
      fileData.append("assetId", trimmedAssetId);
      const resolutions = target.resolutions?.length ? target.resolutions : [target.resolution];
      fileData.append("resolution", resolutions.filter(Boolean).join(","));
      appendOptionalTag(fileData, target.tag);
      await updateAsset(fileData);
      dispatch(setAlert(customConfig.UPDATE_SUCCESS, "success"));
      setStoredReload((value) => value + 1);
    } catch (err) {
      dispatch(setAlert(getAssetErrorMessage(err), "danger"));
    } finally {
      setPendingAction("");
      replaceTargetRef.current = null;
    }
  };

  const confirmDeleteResolution = async () => {
    if (!deleteRow || !trimmedAssetId) {
      return;
    }

    setPendingAction("delete");

    try {
      const resolutions = deleteRow.resolutions?.length
        ? deleteRow.resolutions
        : [deleteRow.resolution].filter(Boolean);

      for (const resolution of resolutions) {
        await deleteAsset(trimmedAssetId, deleteRow.tag, resolution);
      }
      dispatch(setAlert(customConfig.DELETE_SUCCESS, "success"));
      setDeleteRow(null);
      setStoredReload((value) => value + 1);
    } catch (err) {
      dispatch(setAlert(getAssetErrorMessage(err, "Request failed", "Asset not found"), "danger"));
    } finally {
      setPendingAction("");
    }
  };

  const formBody = isUpdate && page ? (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <Box sx={{ mb: 2 }}>
        <SectionSubHeading sx={{ mb: 2 }}>Asset metadata</SectionSubHeading>
        <Grid2 container spacing={2} sx={pageGridSx}>
          <PageField label="Asset ID:">
            <FormInput
              type="text"
              size="small"
              fullWidth
              placeholder="e.g. home-banner-test"
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              disabled={loading}
              helperText=" "
              sx={pageFieldSx}
            />
          </PageField>
          <PageField label="Asset Name:">
            <FormInput
              type="text"
              size="small"
              fullWidth
              placeholder="e.g. Sleep Profile"
              value={assetName}
              onChange={(e) => setAssetName(e.target.value)}
              disabled={loading}
              helperText="Optional"
              sx={pageFieldSx}
            />
          </PageField>
          <PageField label="Description:">
            <FormInput
              type="text"
              size="small"
              fullWidth
              placeholder="e.g. Sleep profile images"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
              helperText="Optional"
              sx={pageFieldSx}
            />
          </PageField>
        </Grid2>
      </Box>

      <Divider sx={{ my: 2 }} />

      <SectionSubHeading sx={{ mb: 1 }}>Stored resolutions</SectionSubHeading>
      <input
        ref={replaceInputRef}
        type="file"
        hidden
        onChange={handleReplaceFile}
      />
      {(storedGroups.length ? storedGroups : storedRows).length ? (
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 2, overflow: "auto" }}>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              <col style={{ width: "28%" }} />
              <col style={{ width: "34%" }} />
              <col style={{ width: "96px" }} />
              <col style={{ width: "96px" }} />
            </colgroup>
            <Box component="thead">
              <Box component="tr">
                {["Tag", "Resolution", "Image", "Actions"].map((label) => (
                  <Box
                    component="th"
                    key={label}
                    scope="col"
                    sx={{
                      ...storedResolutionCellSx,
                      fontWeight: 700,
                      bgcolor: "grey.100",
                      textAlign: label === "Tag" || label === "Resolution" ? "left" : "center",
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody" sx={{ "& tr:last-of-type td": { borderBottom: 0 } }}>
              {(storedGroups.length ? storedGroups : storedRows).map((row) => (
                <Box component="tr" key={row.key}>
                  <Box component="td" sx={storedResolutionCellSx}>
                    {row.tag || "No tag"}
                  </Box>
                  <Box component="td" sx={{ ...storedResolutionCellSx, wordBreak: "break-word" }}>
                    {(row.resolutions || [row.resolution]).filter(Boolean).join(", ")}
                  </Box>
                  <Box component="td" sx={{ ...storedResolutionCellSx, textAlign: "center" }}>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <StoredResolutionThumb url={row.url} />
                    </Box>
                  </Box>
                  <Box component="td" sx={{ ...storedResolutionCellSx, textAlign: "center" }}>
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                      <AppIconButton
                        size="small"
                        variant="primary"
                        tooltip="Replace"
                        aria-label="Replace"
                        disabled={loading}
                        onClick={() => startReplace(row)}
                        icon={<UploadFileIcon fontSize="small" />}
                      />
                      <AppIconButton
                        size="small"
                        variant="danger"
                        tooltip="Delete"
                        aria-label="Delete"
                        disabled={loading}
                        onClick={() => setDeleteRow(row)}
                        icon={<DeleteIcon fontSize="small" />}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No resolutions are stored for this asset yet.
        </Typography>
      )}

      <form onSubmit={handleFileSubmit}>
        <SectionSubHeading sx={{ mb: 2 }}>Add or replace a file</SectionSubHeading>
        <Grid2 container spacing={2} sx={pageGridSx}>
          <PageField label="Tag:">
            <FormInput
              type="text"
              size="small"
              fullWidth
              placeholder="Optional"
              value={tag}
              onChange={handleStoredTagChange}
              disabled={loading}
              helperText="Optional"
              sx={pageFieldSx}
            />
          </PageField>
          <PageField label="Resolution:">
            <FormInput
              type="select"
              multiple
              size="small"
              fullWidth
              value={selectedResolutions}
              onChange={(e) => setSelectedResolutions(e.target.value)}
              options={resolutionOptions}
              renderValue={(value) =>
                (Array.isArray(value) && value.length ? value.join(", ") : "Select resolutions")
              }
              renderOption={(option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    size="small"
                    checked={selectedResolutions.includes(option.value)}
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  {option.label}
                </MenuItem>
              )}
              MenuProps={selectMenuProps}
              disabled={loading}
              helperText="One file is sent for every selected resolution. An existing resolution is replaced."
              sx={pageFieldSx}
            />
          </PageField>
          <PageField label="File Upload:">
            <Box sx={{ width: "100%", minWidth: 0 }}>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                style={pageFileInputStyle}
                disabled={loading}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                {selectedResolutions.length
                  ? `Selected resolutions: ${selectedResolutions.join(", ")}`
                  : "Choose resolutions, then a file, to add or replace them."}
              </Typography>
              {file && replacingResolutions.length ? (
                <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                  {`Already stored and will be replaced: ${replacingResolutions.join(", ")}`}
                </Typography>
              ) : null}
            </Box>
          </PageField>
        </Grid2>
        <Box sx={pageActionsSx}>
          <AppButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={loading}
            loading={pendingAction === "file"}
          >
            Update File
          </AppButton>
          <AppButton
            variant="ghost"
            size="medium"
            type="button"
            onClick={resetForm}
            disabled={loading}
          >
            Reset
          </AppButton>
          {onCancel && (
            <AppButton
              variant="ghost"
              size="medium"
              type="button"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </AppButton>
          )}
        </Box>
      </form>
      <Dialog
        open={Boolean(deleteRow)}
        onClose={() => !pendingAction && setDeleteRow(null)}
        title="Delete resolution"
        showCloseButton
        actions={[
          {
            key: "cancel",
            label: "Cancel",
            variant: "ghost",
            onClick: () => setDeleteRow(null),
            disabled: Boolean(pendingAction),
          },
          {
            key: "delete",
            label: "Delete",
            variant: "primary",
            onClick: confirmDeleteResolution,
            loading: pendingAction === "delete",
          },
        ]}
      >
        <Typography variant="body2">
          {deleteRow
            ? `This deletes ${trimmedAssetId}${deleteRow.tag ? `, tag ${deleteRow.tag}` : ""}, resolution ${(deleteRow.resolutions || [deleteRow.resolution]).filter(Boolean).join(", ")}. Other resolutions stay.`
            : ""}
        </Typography>
      </Dialog>
    </Box>
  ) : isUpdate ? (
    <Box>
      <Box sx={{ mb: 2 }}>
        <SectionSubHeading sx={{ mb: 2 }}>Asset metadata</SectionSubHeading>
        {assetIdField}
        {metadataFields}
      </Box>

      <Divider sx={{ my: 2 }} />

      <form onSubmit={handleFileSubmit}>
        <SectionSubHeading sx={{ mb: 2 }}>Asset file</SectionSubHeading>
        {fileFields}
        {savedAssetIdRow}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <AppButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={loading}
            loading={pendingAction === "file"}
          >
            Update File
          </AppButton>
          <AppButton
            variant="ghost"
            size="medium"
            type="button"
            onClick={resetForm}
            disabled={loading}
          >
            Reset
          </AppButton>
        </Box>
      </form>
    </Box>
  ) : page ? (
    <form
      onSubmit={handleFileSubmit}
      style={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}
    >
      <Grid2 container spacing={2} sx={pageGridSx}>
        <PageField label="Asset Name:">
          <FormInput
            type="text"
            size="small"
            fullWidth
            placeholder="e.g. Sleep Profile"
            value={assetName}
            onChange={(e) => setAssetName(e.target.value)}
            disabled={loading}
            helperText="Optional"
            sx={pageFieldSx}
          />
        </PageField>
        <PageField label="Description:">
          <FormInput
            type="text"
            size="small"
            fullWidth
            placeholder="e.g. Sleep profile images"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
            helperText="Optional"
            sx={pageFieldSx}
          />
        </PageField>
        <PageField label="Tag:">
          <FormInput
            type="text"
            size="small"
            fullWidth
            placeholder="Optional"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            disabled={loading}
            helperText="Optional"
            sx={pageFieldSx}
          />
        </PageField>
        <PageField label="Resolution:">
          <FormInput
            type="select"
            multiple
            size="small"
            fullWidth
            value={selectedResolutions}
            onChange={(e) => setSelectedResolutions(e.target.value)}
            options={ASSET_RESOLUTION_OPTIONS}
            displayEmpty
            renderValue={(value) =>
              (Array.isArray(value) && value.length ? value.join(", ") : "Select resolutions")
            }
            renderOption={(option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox
                  size="small"
                  checked={selectedResolutions.includes(option.value)}
                  sx={{ p: 0.5, mr: 1 }}
                />
                {option.label}
              </MenuItem>
            )}
            MenuProps={selectMenuProps}
            disabled={loading}
            helperText="One file is uploaded for every selected resolution."
            sx={pageFieldSx}
          />
        </PageField>
        <PageField label="File Upload:">
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              style={pageFileInputStyle}
              disabled={loading}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              {selectedResolutions.length
                ? `Selected resolutions: ${selectedResolutions.join(", ")}`
                : "Any file type (images, animations, JSON, video, etc.)"}
            </Typography>
            {imagePreview}
          </Box>
        </PageField>
      </Grid2>
      {savedAssetIdRow}
      <Box sx={pageActionsSx}>
        <AppButton
          variant="primary"
          size="medium"
          type="submit"
          disabled={loading}
          loading={pendingAction === "file"}
        >
          Create Asset
        </AppButton>
        <AppButton
          variant="ghost"
          size="medium"
          type="button"
          onClick={resetForm}
          disabled={loading}
        >
          Reset
        </AppButton>
        {onCancel && (
          <AppButton
            variant="ghost"
            size="medium"
            type="button"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </AppButton>
        )}
      </Box>
    </form>
  ) : (
    <form onSubmit={handleFileSubmit}>
      <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
        <Grid2 size={{ xs: 12, md: 2 }}>
          <LabelText sx={{ fontWeight: 600 }}>Asset ID:</LabelText>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
          <FormInput
            type="text"
            size="small"
            fullWidth
            label="Enter Asset ID"
            placeholder="e.g. 14890e8f-201a-4b39-8f71-e3cacc635f26"
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            disabled={loading}
            helperText="Optional. Sent with the file when you already have an Asset ID."
            sx={fieldSx}
          />
        </Grid2>
      </Grid2>
      {metadataFields}
      {fileFields}
      {savedAssetIdRow}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
        <AppButton
          variant="primary"
          size="medium"
          type="submit"
          disabled={loading}
          loading={pendingAction === "file"}
        >
          Create Asset
        </AppButton>
        <AppButton
          variant="ghost"
          size="medium"
          type="button"
          onClick={resetForm}
          disabled={loading}
        >
          Reset
        </AppButton>
        {onCancel && (
          <AppButton
            variant="ghost"
            size="medium"
            type="button"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </AppButton>
        )}
      </Box>
    </form>
  );

  if (embedded) {
    return formBody;
  }

  return (
    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
      <Alert />
      {formBody}
    </Paper>
  );
}

export default AssetUploadForm;
